import { Box, Button, Card, Grid, IconButton, InputAdornment, Modal, TextField, Tooltip, Typography } from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import { BookingService } from '../Service'
import { Controller, set, useForm } from 'react-hook-form'
import { Calculate, CheckCircle, Close, ContentCopy } from '@mui/icons-material'
import Swal from 'sweetalert2'
import dayjs from 'dayjs'
import { CouponServices } from '../../Marketing/coupons/CouponServices'
import { formatDateTime } from '../../../utils/dateUtils'
import { PermissionGuard } from '../../../components/PermissionGuard'

const CouponAmountCalculator = ({ bookingDetailsdata, fetchBookingDetalById ,label}) => {
    const { control, watch, handleSubmit, formState: { isDirty }, reset } = useForm({
        defaultValues: {
            bookingId: bookingDetailsdata?.id,
            targetAmount: '',
        }
    })
    const [openCalculator, setOpenCalculator] = useState(false);
    const [isCalculating, setIsCalculating] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [calculatorResponse, setCalculatorResponse] = useState(null);
    const calculateAmount = async (data) => {
        setIsCalculating(true);
        try {
            const calculatorResponse = await BookingService.calculateDiscountAmount(data);
            if (calculatorResponse.success == '1') {
                console.log('Calculated Amount:', calculatorResponse.data);
                setCalculatorResponse(calculatorResponse.data);
                setActiveStep(1);
            }
        } catch (error) {
            console.error('Error calculating amount:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to calculate coupon amount. Please try again.',
            });
        } finally {
            setIsCalculating(false);
        }

    }
    useEffect(() => {
        if (bookingDetailsdata?.id > 0) {
            reset({
                bookingId: bookingDetailsdata?.id,
                targetAmount: '0',
            })
        }
    }, [bookingDetailsdata])

    // Timestamp-based code (better uniqueness)
    const characterSets = {
        uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
        mixed: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789',
        readable: 'ABCDEFGHJKMNPQRSTUVWXYZ23456789', // Excludes 0,O,I,1,L
        readableMixed: 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
    };
    const generateTimestampCode = useCallback((length = 8, options = {}) => {
        const { type = 'uppercase' } = options;
        const timestamp = Date.now().toString(36).toUpperCase();
        const chars = characterSets[type] || characterSets.uppercase;

        if (timestamp.length >= length) {
            return timestamp.substring(0, length);
        }

        let result = timestamp;
        while (result.length < length) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return result;
    }, []);

    const [couponDetails, setCouponDetails] = useState(null);

    const createCoupon = async () => {
        const couponBody = {
            title: 'System Generated Discount coupon',
            description: 'Created By OPS Team',
            generatedBy:'system',
            couponCode: generateTimestampCode(6, { type: 'uppercase' }),//SGCDBI
            couponType: 'fixed',//fixed or percentage
            couponValue: 100,
            minimumBookingAmt: 1,
            maximumDiscount: calculatorResponse?.couponAmount,
            startDate: dayjs().format("YYYY-MM-DD"),
            endDate: dayjs().format("YYYY-MM-DD"),
            limitPerUser: '1',
            selectedCustomers: [
                {
                    id: bookingDetailsdata?.customer?.id
                }
            ],
            status: true
        }
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `You want to create coupon for booking id ${bookingDetailsdata?.id} ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            allowOutsideClick: false,
        })
        if (result.isConfirmed) {
            console.log('Coupon Body:', couponBody);
            Swal.fire({
                title: 'Processing...',
                text: `Please wait while creating coupon for booking id ${bookingDetailsdata?.id}`,
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });
            try {
                const response = await CouponServices.addCoupon(couponBody);
                if (response.status == 'success') {
                    console.log('Coupon Created:', response.data);
                    setCouponDetails(response.data.coupon);
                    setActiveStep(2);
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: response.message || 'Failed to create coupon. Please try again.',
                    });
                }
            } catch (error) {
                console.error('Error creating coupon:', error);
            } finally {
                Swal.close();
            }
        }
    }

    const [copied, setCopied] = useState(0);
    const handleCopy = () => {
        navigator.clipboard.writeText(couponDetails.couponCode);
        setCopied(couponDetails.id);
        setTimeout(() => setCopied(0), 2000); // Reset after 2 seconds
    }
    return (
        <Box>
            {label =='text' ? 
                <Typography
                    onClick={() => setOpenCalculator(true)}
                    sx={{
                        fontSize: '.75rem',
                        color: '#1976d2',
                        cursor: 'pointer',
                    }}
                >
                    Calculate Coupon Amount
                </Typography>
                :
                <Tooltip title="Open Coupon Amount Calculator" placement="top-start" arrow>
                    <IconButton 
                        size='small'
                        onClick={() => setOpenCalculator(true)}
                        color='warning'
                    >
                        <Calculate />
                    </IconButton>
                </Tooltip>
            }
            <Modal
                open={openCalculator}
                // onClose={() => setOpenServiceDropdown(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                sx={{ overflow: "scroll" }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: { xs: '50%', sm: '50%' },
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'background.paper',
                        p: 0,
                        width: { xs: "95%", sm: "30%" },
                        minHeight: '25%'
                    }}

                    borderRadius={2}
                >
                    <Box sx={{ borderBottom: '1px solid lightGray', display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '1% 2%' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '.9rem' }} >
                            Coupon Amount Calculator
                        </Typography>
                        <IconButton
                            size='small'
                            onClick={() => {
                                setOpenCalculator(false);
                                setCalculatorResponse(null);
                                reset({
                                    bookingId: bookingDetailsdata?.id,
                                    targetAmount: '0',
                                });
                                setActiveStep(0);
                                setCouponDetails(null);
                                setCopied(0);
                            }}
                            sx={{ p: 0 }}
                            color='error'
                        >
                            <Close sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                        </IconButton>
                    </Box>
                    {activeStep == '2' && couponDetails &&
                        <Box sx={{ p: '2%', display: 'flex', }}>

                            <Grid container spacing={1}>
                                <Grid item xs={12}>
                                    <Card sx={{ p: '1%', bgcolor: '#dadada4d' }}>
                                        <Box sx={{display:'flex', alignItems:'center'}}>
                                            <Typography sx={{ fontWeight: 500, color: '#3E3E3E' ,fontSize:'0.75rem',width:'25%'}} variant='body1'>Coupon Code</Typography>
                                            <Typography sx={{ fontWeight: 500, color: '#3E3E3E',fontSize:'0.75rem',width:'2%' }} variant='body1'>:</Typography>
                                            <Typography sx={{ fontWeight: 500, color: '#fff',fontSize:'0.65rem',bgcolor:'#000',px:1 ,width:"50%",textAlign:'center'}} variant='body1'>{couponDetails?.couponCode}</Typography>
                                            <Tooltip title={copied == couponDetails.id ? "Copied!" : "Copy Code"}>
                                                <IconButton size="small" onClick={handleCopy} sx={{ color: copied == couponDetails.id ? 'green' : 'blue' }}>
                                                    {copied == couponDetails.id ?

                                                        <CheckCircle fontSize="x-small" />
                                                        :
                                                        <ContentCopy fontSize="x-small" />
                                                    }
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                        <Box sx={{display:'flex', alignItems:'center'}}>
                                            <Typography sx={{ fontWeight: 500, color: '#3E3E3E' ,fontSize:'0.75rem',width:'25%'}} variant='body1'>Start Date</Typography>
                                            <Typography sx={{ fontWeight: 500, color: '#3E3E3E' ,fontSize:'0.75rem',width:'2%'}} variant='body1'>:</Typography>
                                            <Typography sx={{ fontWeight: 600, color: '#6E6E6E', fontSize: '0.65rem'}} variant='body1'>{couponDetails?.startDate ? formatDateTime(couponDetails?.startDate, 'date') : 'N/A'}</Typography>
                                        </Box>
                                        <Box sx={{display:'flex', alignItems:'center'}}>
                                            <Typography sx={{ fontWeight: 500, color: '#3E3E3E' ,fontSize:'0.75rem',width:'25%'}} variant='body1'>End Date</Typography>
                                            <Typography sx={{ fontWeight: 500, color: '#3E3E3E' ,fontSize:'0.75rem',width:'2%'}} variant='body1'>:</Typography>
                                            <Typography sx={{ fontWeight: 600, color: '#6E6E6E', fontSize: '0.65rem'}} variant='body1'>{couponDetails?.startDate ? formatDateTime(couponDetails?.endDate, 'date') : 'N/A'}</Typography>
                                        </Box>

                                    </Card>
                                </Grid>

                            </Grid>
                        </Box>
                    }
                    {activeStep == '1' && calculatorResponse &&
                        <Box sx={{ p: '2%', display: 'flex', }}>

                            <Grid container spacing={1}>
                                <Grid item xs={7}>
                                    <Card sx={{ p: '1%', bgcolor: '#dadada4d' }}>
                                        <Box sx={{display:'flex', alignItems:'center'}}>
                                            <Typography sx={{ fontWeight: 500, color: '#3E3E3E' ,fontSize:'0.75rem',width:'50%'}} variant='body1'>Sub Total</Typography>
                                            <Typography sx={{ fontWeight: 500, color: '#3E3E3E' ,fontSize:'0.75rem',width:'3%'}} variant='body1'>:</Typography>
                                            <Typography sx={{ fontWeight: 600, color: '#6E6E6E', fontSize: '0.65rem'}} variant='body1'>₹ {parseFloat(calculatorResponse?.baseSubtotal).toFixed(2)}</Typography>
                                        </Box>
                                        <Box sx={{display:'flex', alignItems:'center'}}>
                                            <Typography sx={{ fontWeight: 500, color: '#3E3E3E' ,fontSize:'0.75rem',width:'50%'}} variant='body1'>Coin Value</Typography>
                                            <Typography sx={{ fontWeight: 500, color: '#3E3E3E' ,fontSize:'0.75rem',width:'3%'}} variant='body1'>:</Typography>
                                            <Typography sx={{ fontWeight: 600, color: '#6E6E6E', fontSize: '0.65rem'}} variant='body1'>₹ {parseFloat(calculatorResponse?.coinValue).toFixed(2)}</Typography>
                                        </Box>
                                        <Box sx={{display:'flex', alignItems:'center'}}>
                                            <Typography sx={{ fontWeight: 500, color: '#3E3E3E' ,fontSize:'0.75rem',width:'50%'}} variant='body1'>Coupon Amount</Typography>
                                            <Typography sx={{ fontWeight: 500, color: '#3E3E3E' ,fontSize:'0.75rem',width:'3%'}} variant='body1'>:</Typography>
                                            <Typography sx={{ fontWeight: 600, color: '#6E6E6E', fontSize: '0.65rem'}} variant='body1'>₹ <Box component={'span'} sx={{color:'#fff',bgcolor:'#000',px:1}}>{parseFloat(calculatorResponse?.couponAmount).toFixed(2)}</Box></Typography>
                                        </Box>
                                        <Box sx={{display:'flex', alignItems:'center'}}>
                                            <Typography sx={{ fontWeight: 500, color: '#3E3E3E' ,fontSize:'0.75rem',width:'50%'}} variant='body1'>Final Total</Typography>
                                            <Typography sx={{ fontWeight: 500, color: '#3E3E3E' ,fontSize:'0.75rem',width:'3%'}} variant='body1'>:</Typography>
                                            <Typography sx={{ fontWeight: 600, color: '#6E6E6E', fontSize: '0.65rem'}} variant='body1'>₹ {parseFloat(calculatorResponse?.calculatedTotal).toFixed(2)}</Typography>
                                        </Box>
                                    </Card>
                                </Grid>
                                <Grid item xs={5} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <PermissionGuard feature='coupon' permission='canCreate'>
                                        <Button size='small' variant='contained' color='primary' onClick={() => createCoupon()}>
                                            Create Coupon
                                        </Button>
                                    </PermissionGuard>
                                </Grid>

                            </Grid>
                        </Box>
                    }

                    {activeStep == '0' && <Box component={'form'} onSubmit={handleSubmit(calculateAmount)} sx={{ p: '2%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, width: '35%' }}>Booking Id</Typography>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, width: '2%' }}>:</Typography>

                            <Controller
                                name="bookingId"
                                control={control}
                                render={({ field, fieldState: { error } }) =>
                                    <TextField
                                        {...field}
                                        fullWidth
                                        size='small'
                                        error={!!error}
                                        InputProps={{
                                            style: { fontSize: '0.75rem' },
                                        }}
                                        disabled
                                    />
                                }
                            />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', my: '1%' }}>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, width: '35%' }}>Final Amount</Typography>
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, width: "2%" }}>:</Typography>

                            <Controller
                                name="targetAmount"
                                control={control}
                                rules={{
                                    required: 'Enter Final Booking Amount',
                                    validate: (value) => {
                                        if (value <= 0) {
                                            return 'Final Booking Amount should be greater than 0';
                                        }


                                        return true;
                                    },
                                }}
                                render={({ field, fieldState: { error } }) =>
                                    <TextField
                                        {...field}
                                        fullWidth
                                        type='number'
                                        size='small'
                                        error={!!error}
                                        disabled={isCalculating}
                                        InputProps={{
                                            style: { fontSize: '0.75rem' },
                                            onWheel: (e) => e.target.blur(),  // Prevent scroll wheel
                                            onKeyDown: (e) => {
                                                if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                    e.preventDefault();
                                                }
                                            },
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Typography sx={{ fontSize: '0.65rem' }}>₹</Typography>
                                                </InputAdornment>
                                            )
                                        }}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // allow decimal value  and remove leading zeros
                                            const formattedValue = value.replace(/^0+/, '');
                                            // const maxValue = formattedValue >= 100 ? 99 : formattedValue;
                                            field.onChange(formattedValue);
                                        }}
                                    />
                                }
                            />
                        </Box>
                        <Box sx={{ textAlign: 'center' }}>
                            <Button
                                size='small'
                                variant='contained'
                                color='primary'
                                type='submit'
                                disabled={!watch('targetAmount') || watch('targetAmount') === 0 || isCalculating || !isDirty}
                            >
                                {isCalculating ? 'Calculating...' : 'Calculate Amount'}
                            </Button>
                        </Box>
                    </Box>

                    }
                </Box>
            </Modal>
        </Box>
    )
}

export default CouponAmountCalculator